import { inject, signal, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { Maybe } from 'graphql/jsutils/Maybe';
import { EMPTY, firstValueFrom } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

import {
  EntityType,
  EventType,
  FetchTrialSitePaymentSchedulesInput,
  fetchTrialSitePaymentSchedulesQuery,
  GqlService,
  listSiteBudgetVersionsQuery,
  NoteType,
  PageInput,
  UpdateNoteInput,
  UpdateSiteBudgetVersionInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { gqlToPromise } from '@shared/utils/gql-to-promise';
import { Utils } from '@shared/utils/utils';

const siteBudgetKeys = {
  index: (trialKey: string) => ['site-budget', trialKey] as const,
  paymentSchedules: (trialKey: string, input: Omit<FetchTrialSitePaymentSchedulesInput, 'page'>) =>
    [...siteBudgetKeys.index(trialKey), 'payment-schedules', input] as const,
};

export const injectSiteBudget = ({ site_id }: { site_id: Signal<Maybe<string>> }) => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);
  const overlayService = inject(OverlayService);

  return injectQuery(() => ({
    enabled: !!site_id(),
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    queryFn: async () => {
      const response = await firstValueFrom(
        gqlService.listSiteBudgetVersions$({ site_id: site_id() as string })
      );

      if (!response.success) {
        throw response.errors;
      }

      return response.data as listSiteBudgetVersionsQuery[];
    },
    queryKey: ['siteBudget', mainQuery.trialKey(), site_id()],
  }));
};

export const injectUpdateSiteBudgetMutation = (silent = false) => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const queryClient = inject(QueryClient);
  const mainQuery = inject(MainQuery);

  return injectMutation(() => ({
    mutationFn: async (params: UpdateSiteBudgetVersionInput & { site_id: string }) => {
      const { site_id: _, ...rest } = params;
      const response = await gqlToPromise(() => gqlService.updateSiteBudgetVersion$(rest));

      return response;
    },
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    onSuccess: (_, { id, site_id, ...restOptimisticUpdate }) => {
      queryClient.setQueryData(
        ['siteBudget', mainQuery.trialKey(), site_id],
        (data?: listSiteBudgetVersionsQuery[]) => {
          return (data || []).map((item) => {
            return item.id === id ? { ...item, ...restOptimisticUpdate } : item;
          });
        }
      );

      if (!silent) {
        overlayService.success('Successfully updated');
      }
    },
  }));
};

export const injectDeleteSiteBudgetVersionMutation = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const queryClient = inject(QueryClient);
  const mainQuery = inject(MainQuery);

  return injectMutation(() => ({
    mutationFn: async ({ id }: { id: string; site_id: string }) => {
      const response = await gqlToPromise(() => gqlService.removeSiteBudgetVersion$(id));

      return response?.id;
    },
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    onSuccess: (_, { id, site_id }) => {
      queryClient.setQueryData(
        ['siteBudget', mainQuery.trialKey(), site_id],
        (data?: listSiteBudgetVersionsQuery[]) => {
          return (data || []).filter((item) => item.id !== id);
        }
      );

      overlayService.success('Successfully deleted');
    },
  }));
};

export const injectProcessSiteBudgetEffectiveDateChangeMutation = () => {
  const eventService = inject(EventService);
  const authQuery = inject(AuthQuery);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async ({
      effectiveDate,
      prevEffectiveDate,
      protocolVersion,
      siteBudgetName,
      siteBudgetVersionId,
    }: {
      effectiveDate: string;
      prevEffectiveDate: string;
      protocolVersion: string;
      siteBudgetName: string;
      siteBudgetVersionId: string;
    }) => {
      const response = await firstValueFrom(
        eventService.processEvent$({
          entity_id: siteBudgetVersionId,
          entity_type: EntityType.SITE_BUDGET_VERSION,
          payload: JSON.stringify({
            newEffectiveDate: effectiveDate,
            previousEffectiveDate: prevEffectiveDate,
            protocolVersion,
            siteBudgetName,
            updatedBy: `${authQuery.getFullName()} ${authQuery.getEmail()}`,
          }),
          type: EventType.SITE_BUDGET_EFFECTIVE_DATE_UPDATED,
        })
      );

      if (response.errors.length) {
        throw response.errors;
      }

      return response;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const injectProcessUpdateInvestigatorTransactionMutation = () => {
  const eventService = inject(EventService);

  return injectMutation(() => ({
    mutationFn: async ({ siteId }: { siteId: string }) => {
      await gqlToPromise(() =>
        eventService.processEvent$({
          entity_id: siteId,
          entity_type: EntityType.SITE,
          type: EventType.UPDATE_INVESTIGATOR_TRANSACTIONS,
        })
      );
    },
  }));
};

export const injectSaveReasonForAmendmentMutation = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const queryClient = inject(QueryClient);
  const mainQuery = inject(MainQuery);

  return injectMutation(() => ({
    mutationFn: async ({
      note,
      previousNote,
      siteBudgetVersionId,
    }: {
      note: { id: null | string; message: string };
      previousNote: string;
      site_id: string;
      siteBudgetVersionId: string;
    }) => {
      const updateSiteBudgetVersionResult = await gqlToPromise(() =>
        gqlService.updateSiteBudgetVersion$({
          id: siteBudgetVersionId,
          message: note.message,
          previous_message: previousNote,
        })
      );

      let createUpdateNoteResult;

      if (note.id) {
        createUpdateNoteResult = await gqlToPromise(() =>
          gqlService.updateNote$(note as UpdateNoteInput)
        );
      } else {
        createUpdateNoteResult = await gqlToPromise(() =>
          gqlService.createNote$({
            entity_id: siteBudgetVersionId,
            entity_type: EntityType.SITE_BUDGET_VERSION,
            message: Utils.scrubUserInput(note.message || ''),
            note_type: NoteType.NOTE_TYPE_GENERAL,
          })
        );
      }

      return [updateSiteBudgetVersionResult, createUpdateNoteResult];
    },
    onError: (error: unknown) => {
      console.error(error);
    },
    onSuccess: (data, optimisticUpdate) => {
      const { note, site_id, siteBudgetVersionId } = optimisticUpdate;

      const noteId = data[1]?.id;

      if (!noteId) {
        return;
      }

      const optimisticNote = {
        id: noteId,
        message: note.message,
      };

      queryClient.setQueryData(
        ['siteBudget', mainQuery.trialKey(), site_id],
        (data?: listSiteBudgetVersionsQuery[]) => {
          return (data || []).map((item) => {
            return item.id === siteBudgetVersionId ? { ...item, notes: [optimisticNote] } : item;
          });
        }
      );

      overlayService.success('Successfully saved!');
    },
  }));
};

export const injectTrialSitePaymentSchedules = (
  input: Signal<Omit<FetchTrialSitePaymentSchedulesInput, 'page'>>,
  enabled: Signal<boolean> = signal(true)
) => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const mainQuery = inject(MainQuery);

  return injectQuery(() => ({
    enabled: enabled(),
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    queryFn: async () => {
      const page_input: PageInput = {
        limit: 3000,
        offset: 0,
      };

      const inputValue = {
        ...input(),
        page: page_input,
      };

      const response = await gqlToPromise(() => {
        return gqlService.fetchTrialSitePaymentSchedules$(inputValue).pipe(
          expand(({ data, success }) => {
            if (success && data) {
              if (Number.isInteger(data.next_offset) && data.next_offset >= 0) {
                inputValue.page.offset = data.next_offset;

                return gqlService.fetchTrialSitePaymentSchedules$(inputValue);
              }
            }

            return EMPTY;
          }),
          reduce(
            (
              acc: {
                data: fetchTrialSitePaymentSchedulesQuery['items'] | null;
                errors: string[];
                success: boolean;
              },
              curr
            ) => {
              if (acc.data && curr.success && curr.data) {
                acc.data = acc.data.concat(curr.data.items);

                return acc;
              }

              return {
                data: null,
                errors: curr.errors,
                success: false,
              };
            },
            {
              data: [] as fetchTrialSitePaymentSchedulesQuery['items'] | null,
              errors: [] as string[],
              success: true,
            }
          )
        );
      });

      return response;
    },
    queryKey: siteBudgetKeys.paymentSchedules(mainQuery.trialKey(), input()),
  }));
};
